import {ResultOptions} from "./QueryResultOptions";
import {isTemplateLineItem, isTimedLineItem, LineItem, TemplateLineItem, TimedLineItem} from "../../line-items";
import {LineItemColumn, LineItemRow, QueryResultData, ResultCell, TimeColumn} from "../QueryResult";
import {normalizeString} from "../../line-item-utils/coding.utils";
import {values} from "ramda";
import {CELL_TEXT_FOR_ITEM_EXCLUDED_FROM_ROW_AGGREGATE, ROW_AGGREGATE_COLUMN_ID} from "./queryRunnerCommons";
import {AggregatorMethod, lineItemAggregatorMap} from "../../line-item-utils/aggregators";
import {TableBuilder} from "./TableBuilder";

export class TimeSeriesTableBuilder extends TableBuilder {


  public buildTable(): QueryResultData {
    return {
      rows: this.buildRows(this.options),
      columns: this.buildColumns(this.options)
    }
  }


  private buildRows(options: ResultOptions) {
    let lineItems = this.visibleLineItems;

    let rows: LineItemRow[] = [];

    for (let i = 0; i < lineItems.length; i++) {
      let lineItem = lineItems[i];
      let nameCell: ResultCell = this.buildNameCell(lineItem, options);
      let timeValuesCell = this.timeValuesCells(lineItem, options);

      let row: any = {
        name: nameCell
      };

      if (options.showGranularity) {
        row.granularity = this.granularityCell(lineItem);
      }

      if (options.table?.aggregateColumnConfig) {
        row[ROW_AGGREGATE_COLUMN_ID] = this.rowAggregateCell(lineItem, timeValuesCell, options);
      }

      Object.assign(row, this.metadataCells(lineItem), timeValuesCell);

      if (options.withLineItemNameColumn) {
        row.lineItemName = this.buildLineItemNameColumn(lineItem);
      }

      rows.push(row);
    }

    return rows;
  }


  private buildColumns(options: ResultOptions): LineItemColumn[] {
    let query = this.query!;
    let columns: LineItemColumn[] = [];

    columns.push({
      type: 'header',
      columnId: 'name',
      text: 'Line Item'
    });

    if (options.withLineItemNameColumn) {
      columns.push({
        type: 'header',
        columnId: 'lineItemName',
        text: 'Line Item Name'
      });
    }

    if (options.withMetadata) {
      columns.push(...this.buildMetadataColumns(options.withMetadata));
    }

    if (options.showGranularity) {
      columns.push({
        type: 'header',
        columnId: 'granularity',
        text: 'Granularity'
      });
    }

    if (options.table?.aggregateColumnConfig) {
      columns.push({
        type: 'header',
        columnId: ROW_AGGREGATE_COLUMN_ID,
        text: 'Aggregate'
      })
    }


    this.visibleTimeLine.forEach((time) => {
      const timeColumn: TimeColumn & LineItemColumn = {
        type: 'time',
        columnId: time.date.getTime().toString(),
        text: time.fDate,
        time: time.date.getTime(),
        timeIndex: time.time
      }
      columns.push(timeColumn);
    });


    return columns;
  }


  private buildLineItemNameColumn(li: LineItem): ResultCell {
    return {
      columnId: 'lineItemName',
      text: li.name,
      type: 'header',
      value: li.name
    }
  }


  private granularityCell(lineItem: LineItem | (LineItem & TimedLineItem)): ResultCell {
    if (isTimedLineItem(lineItem) || isTemplateLineItem(lineItem)) {
      return {
        columnId: 'granularity',
        text: (lineItem as (TemplateLineItem | TimedLineItem)).getTimeDefinition().granularity,
        type: 'metadata',
        value: (lineItem as (TemplateLineItem | TimedLineItem)).getTimeDefinition().granularity
      }
    } else {
      return {
        columnId: 'granularity',
        text: '',
        type: 'metadata',
        value: ''
      }
    }
  }

  getFieldStr(liName: string, fieldName: string) {
    return this.execution!.getFieldStr(liName, fieldName);
  }


  private rowAggregateCell(lineItem: LineItem, timeValuesCell: Record<string, ResultCell>, options: ResultOptions): ResultCell {
    if(options.table?.aggregateColumnConfig?.excludedItems?.map(normalizeString)?.includes(normalizeString(lineItem.name))){
      return {
        columnId: ROW_AGGREGATE_COLUMN_ID,
        text: CELL_TEXT_FOR_ITEM_EXCLUDED_FROM_ROW_AGGREGATE,
        type: 'metadata',
        value: 0
      }
    }
    let aggregatorMethod: AggregatorMethod = "sum";
    if (isTimedLineItem(lineItem)) {
      aggregatorMethod = lineItem.getTimeDefinition().aggregator;
    }

    let value = lineItem.getTotal(this.store.timeIndex.getRange(), this.execution!, lineItemAggregatorMap({}, aggregatorMethod)(lineItem));

    let formatterFunction = this.getFormattingFunction(lineItem);

    return {
      columnId: ROW_AGGREGATE_COLUMN_ID,
      text: formatterFunction(value),
      type: 'metadata',
      value: value
    }
  }

  private timeValuesCells(lineItem: LineItem, options: ResultOptions): Record<string, ResultCell> {

    let formatterFunction = this.getFormattingFunction(lineItem);

    let cells: Record<string, ResultCell> = {};

    this.visibleTimeLine.forEach((time) => {
      let value = this.execution!.getLineItemValue(
        lineItem,
        time.date.getTime(),
        this.timeIndex.getUnit()
      ).value;

      cells[time.date.getTime().toString()] = {
        type: 'lineItemValue',
        columnId: time.date.getTime().toString(),
        text: formatterFunction(value),
        value: value,
      }
    });

    return cells;
  }

}